import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Icon, PreviewModal } from '@components';
import { allowedFileTypes } from '@components/file-upload/constants';
import FormStepper from '@components/form-stepper/FormStepper';
import { ErrorCode } from '@constants/errorCodes';
import {
  allowedDocumentFileSize,
  allowedImageSizeLabel
} from '@constants/file';
import {
  NAME_MIN_LENGTH,
  OrgStatus,
  SubscriptionPlanExpiredMessage,
  SubscriptionStatus,
  UserFlow
} from '@constants/generic';
import UserPermissionTypes from '@constants/permissions';
import { SUBSCRIPTIONS } from '@constants/routes';
import { LOCAL_STORAGE_KEYS } from '@constants/storage';
import {
  Event,
  KycDetailsFlowContext,
  MixpanelEventV2,
  sendMixpanelEvent,
  sendMixpanelEventV2
} from '@core/analytics/mixpanel';
import {
  businessLicenseDropdownOptions,
  certificateOptions,
  CertificateType
} from '@features/authentication/constants/common';
import {
  trackBusinessLicenseAttached,
  trackBusinessLicenseDeleted,
  trackKycBankDetailsPageViewed,
  trackKycDetailsEnteredAndContinued,
  trackKycDetailsPageViewed,
  trackKycSubmitted,
  trackPanGstFileAttached,
  trackPanGstFileDeleted,
  useBankEditTracker,
  useBankVerificationTracker
} from '@features/authentication/helpers/kycAnalytics';
import {
  useLazyGetKycDetailsQuery,
  useUpgradeLiteMutation,
  useVerifyGstinMutation,
  useVerifyPanMutation
} from '@features/authentication/queries/AuthQuery';
import { UploadingDocument } from '@features/branch-management/components/supporting-document-upload/SupportingDocumentUpload';
import { useLazyGetPresignedUrlQuery } from '@features/branch-management/queries';
import { useLazyGetAllGroupsQuery } from '@features/group-management/queries/GroupQuery';
import useNotify from '@hooks/notify';
import Modal from '@layouts/modal/Modal.v2';
import { ApiError, FieldError } from '@types';
import { downloadFileFromUrlUsingBlob } from '@utils/downloadFile';
import {
  isPasswordProtectedPdf,
  PASSWORD_PROTECTED_FILE_ERROR
} from '@utils/pdf';
import { buildQueryString } from '@utils/queryStringBuilder';
import { getLocalStorageItem } from '@utils/storage';
import { isInvalidGstin, isInvalidPan } from '@utils/validations';

import {
  BankInfoDefaultValue,
  VerifiedBankInfoDefaultValue
} from '../constants';
import useVerify from '../hooks/verify';
import { BankInfoType, VerifiedBankInfoType } from '../types';
import BankAccountDetails from './BankAccountDetails';
import BusinessTypeSelector from './BusinessTypeSelector';
import PanCardDetails from './panCardDetails';

import theme from '../../../../themes/colors';

interface UploadedFileInfo {
  file_path: string;
  type: 'PAN' | 'GST_CERTIFICATE' | 'SUPPORTING_DOCUMENT' | 'BUSINESS_LICENSE';
  document_sub_type?: string;
}

interface SubmitKycModalProps {
  onClose: () => void;
  isDocumentFailed?: boolean;
  flowSource?: 'banner';
}

const steps = [{ label: 'KYC details' }, { label: 'Bank details' }];

const SubmitKycModal = ({
  onClose,
  isDocumentFailed,
  flowSource
}: SubmitKycModalProps) => {
  const navigate = useNavigate();
  const { createErrorAlert } = useNotify();
  const { rolesAndPermissions, currentSubscription, currentOrg, currentUser } =
    useSelector((state: any) => state.user);
  const organizationId = getLocalStorageItem(
    LOCAL_STORAGE_KEYS.ORGANIZATION_ID
  );

  const getFlowContext = (): KycDetailsFlowContext => {
    if (
      getLocalStorageItem(LOCAL_STORAGE_KEYS.USER_FLOW) ===
      UserFlow.UPGRADE_TO_ORG
    ) {
      return 'upgrade_to_org';
    }
    if (isDocumentFailed) return 'resubmission';
    if (flowSource === 'banner') return 'kyc_via_banner';
    return 'initial_onboarding';
  };

  const [currentStep, setCurrentStep] = useState(0);

  // KYC Details state
  const [pan, setPan] = useState('');
  const [savedPan, setSavedPan] = useState('');
  const [panError, setPanError] = useState('');
  const [panFile, setPanFile] = useState<File | null>(null);
  const [supportingDocs, setSupportingDocs] = useState<UploadingDocument[]>([]);
  const [remarks, setRemarks] = useState('');

  // Bank Details state
  const [bankInfo, setBankInfo] = useState<BankInfoType>(BankInfoDefaultValue);
  const [fieldError, setFieldError] = useState<FieldError>({
    field: null,
    message: ''
  });
  const [verifiedName, setVerifiedName] = useState<VerifiedBankInfoType>(
    VerifiedBankInfoDefaultValue
  );
  const [isVerified, setIsVerified] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [savedBankInfo, setSavedBankInfo] =
    useState<BankInfoType>(BankInfoDefaultValue);
  const [savedVerifiedName, setSavedVerifiedName] =
    useState<VerifiedBankInfoType>(VerifiedBankInfoDefaultValue);

  const panFileRef = useRef<HTMLInputElement>(null);
  const businessLicenseFileRef = useRef<HTMLInputElement>(null);

  // Business license state
  const [businessLicenseType, setBusinessLicenseType] = useState<string>(
    businessLicenseDropdownOptions[0].value
  );
  const [businessLicenseFile, setBusinessLicenseFile] = useState<File | null>(
    null
  );

  const [orgName, setOrgName] = useState('');
  const [certificateType, setCertificateType] = useState<string>(
    certificateOptions[0].key
  );
  const [gstin, setGstin] = useState('');
  const [savedGstin, setSavedGstin] = useState('');
  const [gstinError, setGstinError] = useState('');
  const [gstinFile, setGstinFile] = useState<File | null>(null);
  const gstinFileRef = useRef<HTMLInputElement>(null);
  const [isReVerifyKyc, setIsReVerifyKyc] = useState(false);

  // Uploaded files to send on submit
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFileInfo[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const [rejectedReason, setRejectedReason] = useState('');
  const [hasPriorPanDoc, setHasPriorPanDoc] = useState(false);
  const [hasPriorGstinDoc, setHasPriorGstinDoc] = useState(false);
  const [hasPriorBusinessLicenseDoc, setHasPriorBusinessLicenseDoc] =
    useState(false);
  const [existingDocs, setExistingDocs] = useState<
    { type: string; url: string }[]
  >([]);
  const [selectedBusinessType, setSelectedBusinessType] = useState<
    'organisation' | 'individual'
  >('individual');
  const [showPreviewModal, setShowPreviewModal] = useState<boolean>(false);
  const submitAttemptRef = useRef(0);

  const { handleVerifyBank, isVerifyBankLoading } = useVerify();
  const [getPresignedUrl] = useLazyGetPresignedUrlQuery();
  const [onboardOrganization, { isLoading: isUpgradeLiteLoading }] =
    useUpgradeLiteMutation();
  const [getKycDetails] = useLazyGetKycDetailsQuery();
  const [getAllGroups] = useLazyGetAllGroupsQuery();
  const [verifyPan, { isLoading: isVerifyPanLoading }] = useVerifyPanMutation();
  const [verifyGstin, { isLoading: isVerifyGstinLoading }] =
    useVerifyGstinMutation();

  const getKycAnalytics = useCallback(
    () => ({
      flowContext: getFlowContext(),
      profileName: currentUser?.name || '',
      businessName: orgName || currentOrg?.name || '',
      mobileNumber:
        currentUser?.mobile ||
        getLocalStorageItem(LOCAL_STORAGE_KEYS.MOBILE) ||
        ''
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      currentUser?.name,
      currentUser?.mobile,
      orgName,
      currentOrg?.name,
      isDocumentFailed,
      flowSource
    ]
  );

  const trackVerifyAttempt = useBankVerificationTracker(getKycAnalytics);
  const trackEditAttempt = useBankEditTracker(getKycAnalytics);

  useEffect(() => {
    trackKycDetailsPageViewed(getKycAnalytics());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (currentStep !== 1) return;
    trackKycBankDetailsPageViewed(getKycAnalytics());
  }, [currentStep, getKycAnalytics]);

  useEffect(() => {
    if (!organizationId) return;

    const fetchKycDetails = async () => {
      const result = await getKycDetails({ orgId: organizationId });
      const { data } = result;
      if (data) {
        setIsReVerifyKyc(true);
        if (data.pan) {
          setPan(data.pan);
          setSavedPan(data.pan);
        }
        if (data.gstin) {
          setGstin(data.gstin);
          setSavedGstin(data.gstin);
          setCertificateType(CertificateType.GSTIN);
        }
        if (data.remarks) setRemarks(data.remarks);
        if (isDocumentFailed && data.rejected_reason)
          setRejectedReason(data.rejected_reason);
        if (data.bank_account_number) {
          setBankInfo({
            accountNumber: data.bank_account_number,
            confirmAccountNumber: data.bank_account_number,
            ifsc: data.bank_ifsc || ''
          });
        }
        if (data.bank_account_name) {
          setVerifiedName((prev) => ({
            ...prev,
            userName: data.bank_account_name,
            bankBranchName: data.bank_branch_name,
            bankAddress: data.bank_address,
            bankName: data.bank_address || data.bank_branch_name
          }));
          if (data.bank_account_number) {
            setIsVerified(true);
          }
        }
        const hasPan = data.kyc_documents?.some(
          (doc: { type: string }) => doc.type === 'PAN'
        );
        if (hasPan) setHasPriorPanDoc(true);
        const hasGstin = data.kyc_documents?.some(
          (doc: { type: string }) => doc.type === 'GST_CERTIFICATE'
        );
        if (hasGstin) setHasPriorGstinDoc(true);
        const hasBusinessLicense = data.kyc_documents?.some(
          (doc: { type: string }) => doc.type === 'BUSINESS_LICENSE'
        );
        if (hasBusinessLicense) {
          setHasPriorBusinessLicenseDoc(true);
          setSelectedBusinessType('organisation');
        }
        if (data.business_name) setOrgName(data.business_name);
        setExistingDocs(data.kyc_documents);
      }
      if (isDocumentFailed) {
        sendMixpanelEventV2(MixpanelEventV2.RESUBMIT_KYC_CLICKED, {
          mobile_number: currentUser?.mobile || '',
          country_code: '91',
          flow_context: getFlowContext(),
          profile_name: currentUser?.name || '',
          business_name: currentOrg?.name || data?.business_name || '',
          failure_reason: data?.rejected_reason || ''
        });
      } else if (flowSource === 'banner') {
        const branchId =
          getLocalStorageItem(LOCAL_STORAGE_KEYS.BRANCH_ID) || '';
        const groupsRes = await getAllGroups({
          params: { organizationId, branchId },
          queryString: buildQueryString({ status: 'all', limit: 1, page: 1 })
        });
        sendMixpanelEventV2(MixpanelEventV2.KYC_MODAL_OPENED, {
          mobile_number: currentUser?.mobile || '',
          country_code: '91',
          profile_name: currentUser?.name || '',
          group_created_count: groupsRes.data?.pagination?.total_rows ?? 0
        });
      }
    };
    fetchKycDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDocumentFailed, organizationId, getKycDetails]);

  const validateFile = async (file: File) => {
    if (file.size > allowedDocumentFileSize) {
      return `File size exceeds ${allowedImageSizeLabel}`;
    }
    if (!allowedFileTypes.includes(file.type)) {
      return 'Unsupported file type';
    }
    if (await isPasswordProtectedPdf(file)) {
      return PASSWORD_PROTECTED_FILE_ERROR;
    }
    return null;
  };

  const onPanChange = (value: string) => {
    setPanError('');
    setPan(value.toUpperCase());
  };

  const onGstinChange = (value: string) => {
    setGstinError('');
    setGstin(value.toUpperCase());
  };

  const onBankInputChange = (field: string, value: string | number) => {
    setIsVerified(false);
    setBankInfo((prev: any) => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePanFileSelect = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e?.target?.files?.[0];
    if (file) {
      const error = await validateFile(file);
      if (error) {
        setPanError(error);
        if (error === PASSWORD_PROTECTED_FILE_ERROR) createErrorAlert(error);
        trackPanGstFileAttached(getKycAnalytics(), {
          type: 'PAN',
          status: 'failed',
          failureReason: error
        });
        return;
      }
      setPanFile(file);
      setPanError('');
      trackPanGstFileAttached(getKycAnalytics(), {
        type: 'PAN',
        status: 'success'
      });
    }
  };

  const handleGstinFileSelect = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e?.target?.files?.[0];
    if (file) {
      const error = await validateFile(file);
      if (error) {
        setGstinError(error);
        if (error === PASSWORD_PROTECTED_FILE_ERROR) createErrorAlert(error);
        trackPanGstFileAttached(getKycAnalytics(), {
          type: 'GST',
          status: 'failed',
          failureReason: error
        });
        return;
      }
      setGstinFile(file);
      setGstinError('');
      trackPanGstFileAttached(getKycAnalytics(), {
        type: 'GST',
        status: 'success'
      });
    }
  };

  const removePanFile = () => {
    trackPanGstFileDeleted(getKycAnalytics(), { type: 'PAN' });
    setPanFile(null);
    if (panFileRef.current) panFileRef.current.value = '';
  };

  const removeGstinFile = () => {
    trackPanGstFileDeleted(getKycAnalytics(), { type: 'GST' });
    setGstinFile(null);
    if (gstinFileRef.current) gstinFileRef.current.value = '';
  };

  const handleExistingDocDownload = useCallback(
    async (docType: string) => {
      const doc = existingDocs.find((d) => d.type === docType);
      if (!doc?.url) return;
      try {
        const fileName = decodeURIComponent(
          doc.url.split('/').pop()?.split('?')[0] || docType
        );
        await downloadFileFromUrlUsingBlob(doc.url, fileName);
      } catch {
        createErrorAlert('Error downloading file');
      }
    },
    [existingDocs, createErrorAlert]
  );

  const removeExistingPanDoc = () => {
    trackPanGstFileDeleted(getKycAnalytics(), {
      type: certificateType === CertificateType.PAN ? 'PAN' : 'GST'
    });
    setExistingDocs(existingDocs.filter((doc) => doc.type !== 'PAN'));
  };

  const handleBusinessLicenseFileSelect = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e?.target?.files?.[0];
    if (file) {
      const error = await validateFile(file);
      if (error) {
        createErrorAlert(error);
        trackBusinessLicenseAttached(getKycAnalytics(), {
          licenseType: businessLicenseType || '',
          status: 'failed',
          failureReason: error
        });
        return;
      }
      setBusinessLicenseFile(file);
      trackBusinessLicenseAttached(getKycAnalytics(), {
        licenseType: businessLicenseType || '',
        status: 'success'
      });
    }
  };

  const removeBusinessLicenseFile = () => {
    trackBusinessLicenseDeleted(getKycAnalytics(), {
      licenseType: businessLicenseType || ''
    });
    setBusinessLicenseFile(null);
    if (businessLicenseFileRef.current)
      businessLicenseFileRef.current.value = '';
  };

  const removeExistingBusinessLicenseDoc = () => {
    trackBusinessLicenseDeleted(getKycAnalytics(), {
      licenseType: businessLicenseType || ''
    });
    setExistingDocs(
      existingDocs.filter((doc) => doc.type !== 'BUSINESS_LICENSE')
    );
  };

  const handleBusinessTypeChange = (type: 'organisation' | 'individual') => {
    setSelectedBusinessType(type);
    if (type === 'organisation') return;
    setBusinessLicenseType(businessLicenseDropdownOptions[0].value);
    setOrgName('');
    setBusinessLicenseFile(null);
    if (businessLicenseFileRef.current)
      businessLicenseFileRef.current.value = '';
  };

  const uploadToPresignedUrl = useCallback(
    async (presignedUrl: string, file: File): Promise<boolean> => {
      try {
        const response = await fetch(presignedUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type,
            Origin: window.location.origin,
            Referer: window.location.href
          }
        });
        return response.ok;
      } catch {
        return false;
      }
    },
    []
  );

  const uploadFileAndGetPath = useCallback(
    async (
      file: File,
      type:
        | 'PAN'
        | 'GST_CERTIFICATE'
        | 'SUPPORTING_DOCUMENT'
        | 'BUSINESS_LICENSE'
    ): Promise<UploadedFileInfo | null> => {
      try {
        const presignedUrlResponse = await getPresignedUrl({
          organizationId: organizationId || '',
          fileName: file.name
        });

        if (!presignedUrlResponse.data) {
          createErrorAlert('Failed to get upload URL. Please try again.');
          return null;
        }

        const responseData = presignedUrlResponse.data;
        const { file_id: fileId, access_key: accessKey } = responseData;

        const uploadResult = await uploadToPresignedUrl(
          responseData.presigned_url,
          file
        );

        if (!uploadResult) {
          createErrorAlert('Failed to upload file. Please try again.');
          return null;
        }

        return {
          file_path: `${fileId}|${accessKey}`,
          type,
          document_sub_type:
            type === 'BUSINESS_LICENSE' ? businessLicenseType : ''
        };
      } catch {
        createErrorAlert('Error uploading file. Please try again.');
        return null;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [organizationId, getPresignedUrl, uploadToPresignedUrl, createErrorAlert]
  );

  const handleNext = async () => {
    const files: UploadedFileInfo[] = [];

    if (certificateType === CertificateType.PAN) {
      if (isInvalidPan(pan)) {
        setPanError('Invalid PAN');
        return;
      }

      if ((savedPan || savedGstin) && pan !== savedPan) {
        const response = await verifyPan({
          orgId: organizationId || '',
          body: {
            pan
          }
        });
        if ('error' in response) {
          const error = response.error as ApiError;
          const errorMsg = error.data?.message || 'Error verifying PAN';
          createErrorAlert(errorMsg);
          return;
        }
      }

      setIsUploading(true);

      if (panFile) {
        const panUploadResult = await uploadFileAndGetPath(panFile, 'PAN');
        if (!panUploadResult) {
          setIsUploading(false);
          return;
        }
        files.push(panUploadResult);
      }
    }

    if (certificateType === CertificateType.GSTIN) {
      if (isInvalidGstin(gstin)) {
        setGstinError('Invalid GSTIN');
        return;
      }
      if (gstin !== savedGstin) {
        const response = await verifyGstin({
          orgId: organizationId || '',
          body: {
            gstin
          }
        });
        if ('error' in response) {
          const error = response.error as ApiError;
          const errorMsg = error.data?.message || 'Error in verification';
          createErrorAlert(errorMsg);
          return;
        }
      }
      if (gstinFile) {
        const gstinUploadResult = await uploadFileAndGetPath(
          gstinFile,
          'GST_CERTIFICATE'
        );
        if (!gstinUploadResult) {
          setIsUploading(false);
          return;
        }
        files.push(gstinUploadResult);
      }
    }

    if (selectedBusinessType === 'organisation' && businessLicenseFile) {
      const blUploadResult = await uploadFileAndGetPath(
        businessLicenseFile,
        'BUSINESS_LICENSE'
      );
      if (!blUploadResult) {
        setIsUploading(false);
        return;
      }
      files.push(blUploadResult);
    }

    const successfulDocs = supportingDocs.filter(
      (doc) => doc.status === 'success'
    );
    const supportingDocResults = await Promise.all(
      successfulDocs.map((doc) =>
        uploadFileAndGetPath(doc.file, 'SUPPORTING_DOCUMENT')
      )
    );

    const hasFailure = supportingDocResults.some((result) => result === null);
    if (hasFailure) {
      setIsUploading(false);
      return;
    }

    supportingDocResults.forEach((result) => {
      if (result) files.push(result);
    });

    setUploadedFiles(files);
    setIsUploading(false);
    trackKycDetailsEnteredAndContinued(getKycAnalytics(), {
      updatedIndividualToOrgAccount: false
    });
    setCurrentStep(1);
  };

  const verifyBankDetails = () => {
    handleVerifyBank({
      bankInfo,
      setFieldError,
      setVerifiedName,
      setIsVerified,
      pan,
      onVerifyAttempt: trackVerifyAttempt
    });
  };

  const handleBankDetailsEdit = () => {
    trackEditAttempt({
      verifiedBankName:
        verifiedName.bankAddress ||
        verifiedName.bankName ||
        verifiedName.bankBranchName ||
        '',
      verifiedName: verifiedName.userName || ''
    });
    setSavedBankInfo({ ...bankInfo });
    setSavedVerifiedName({
      ...verifiedName,
      bankBranchName: verifiedName.bankBranchName
    });
    setIsEditing(true);
    setIsVerified(false);
    setVerifiedName(VerifiedBankInfoDefaultValue);
  };

  const handleSubmit = async () => {
    if (currentSubscription?.status === SubscriptionStatus.EXPIRED) {
      createErrorAlert(SubscriptionPlanExpiredMessage);
      trackKycSubmitted(getKycAnalytics(), {
        status: 'failed',
        failureReason: SubscriptionPlanExpiredMessage,
        ...(isDocumentFailed && {
          attemptNumber: (submitAttemptRef.current += 1)
        })
      });
      if (
        rolesAndPermissions.permissions.includes(
          UserPermissionTypes.SUBSCRIPTION_VIEW
        )
      ) {
        navigate(SUBSCRIPTIONS.PLAN_DETAILS);
      }
      return;
    }

    const selectedCertificateType =
      certificateType === CertificateType.PAN ? 'PAN' : 'GST_CERTIFICATE';
    const kycDocumentsToSend = uploadedFiles.filter(
      (doc) =>
        doc.type === selectedCertificateType ||
        doc.type === 'SUPPORTING_DOCUMENT' ||
        doc.type === 'BUSINESS_LICENSE'
    );

    const response = await onboardOrganization({
      orgId: organizationId,
      body: {
        primary_account: verifiedName.accountDetailId,
        branch_name: verifiedName.bankName,
        remarks,
        ...(kycDocumentsToSend.length > 0 && {
          kyc_documents: kycDocumentsToSend
        }),
        ...(selectedBusinessType === 'organisation' &&
          businessLicenseType && {
            business_license_type: businessLicenseType
          }),
        ...(selectedBusinessType === 'organisation' && {
          organisation_name: orgName.trim(),
          ...(!isReVerifyKyc && { organisation_type: 'organisation' })
        })
      }
    });

    if ('data' in response) {
      trackKycSubmitted(getKycAnalytics(), {
        status: 'success',
        ...(isDocumentFailed && {
          attemptNumber: (submitAttemptRef.current += 1)
        })
      });
      sendMixpanelEvent(Event.KYC_DETAILS_VERIFIED, { organizationId });
      window.location.reload();
    } else if ('error' in response) {
      const error = response.error as ApiError;
      createErrorAlert(error.data?.message);
      trackKycSubmitted(getKycAnalytics(), {
        status: 'failed',
        failureReason: error.data?.message || 'Error submitting KYC',
        ...(isDocumentFailed && {
          attemptNumber: (submitAttemptRef.current += 1)
        })
      });
      if (error?.data?.errorCode === ErrorCode.ERR_SUBSCRIPTION_EXPIRED) {
        if (
          rolesAndPermissions.permissions.includes(
            UserPermissionTypes.SUBSCRIPTION_VIEW
          )
        ) {
          navigate(SUBSCRIPTIONS.PLAN_DETAILS);
        }
      }
      sendMixpanelEvent(Event.KYC_VERIFICATION_ERROR, {
        organizationId,
        error: error.data?.errorCode
      });
    }
  };

  const isBusinessLicenseRequired =
    selectedBusinessType === 'organisation' &&
    (!businessLicenseType ||
      (!businessLicenseFile && !hasPriorBusinessLicenseDoc));
  const isNextDisabled =
    (certificateType === CertificateType.PAN ? !pan : !gstin) ||
    (certificateType === CertificateType.GSTIN
      ? !gstinFile && !hasPriorGstinDoc
      : !panFile && !hasPriorPanDoc) ||
    isBusinessLicenseRequired ||
    (selectedBusinessType === 'organisation' &&
      orgName.trim().length < NAME_MIN_LENGTH);

  const hasValueInBankDetails =
    bankInfo.accountNumber || bankInfo.confirmAccountNumber || bankInfo.ifsc;
  const hasChangesInBankDetails =
    bankInfo.accountNumber !== savedBankInfo.accountNumber ||
    bankInfo.confirmAccountNumber !== savedBankInfo.confirmAccountNumber ||
    bankInfo.ifsc !== savedBankInfo.ifsc;
  const isVerifyDisabled = !hasValueInBankDetails || !hasChangesInBankDetails;
  const isSubmitDisabled = !isVerified;
  const isNextLoading =
    isUploading ||
    isVerifyBankLoading ||
    isVerifyPanLoading ||
    isVerifyGstinLoading;
  const isSubmitLoading = isUpgradeLiteLoading;

  const isLite =
    currentOrg?.status === OrgStatus.KYC_PENDING ||
    currentOrg?.status === OrgStatus.KYC_SUBMITTED;

  const handleClose = () => {
    const closeEventProperties = {
      mobile_number: currentUser?.mobile || '',
      country_code: '91',
      flow_context: getFlowContext(),
      profile_name: currentUser?.name || '',
      business_name: currentOrg?.name || '',
      step_abandoned_at: steps[currentStep].label
    };

    if (isDocumentFailed) {
      sendMixpanelEventV2(
        MixpanelEventV2.RESUBMIT_KYC_CANCELLED_DISCARDED,
        closeEventProperties
      );
    } else {
      sendMixpanelEventV2(
        MixpanelEventV2.KYC_MODAL_DISCARDED,
        closeEventProperties
      );
    }
    onClose();
  };

  return (
    <Modal
      title="Submit KYC"
      className="w-150"
      onClose={handleClose}
      primaryButtonLabel={currentStep === 0 ? 'Next' : 'Submit'}
      primaryButtonClick={currentStep === 0 ? handleNext : handleSubmit}
      primaryButtonDisabled={
        currentStep === 0 ? isNextDisabled : isSubmitDisabled
      }
      primaryButtonLoading={currentStep === 0 ? isNextLoading : isSubmitLoading}
      secondaryButtonLabel={currentStep === 0 ? 'Discard' : 'Back'}
      secondaryButtonClick={
        currentStep === 0 ? handleClose : () => setCurrentStep(0)
      }
      isClickOutsideRequired={false}
    >
      {isDocumentFailed && (
        <div className="mb-6 flex items-center gap-2 rounded-lg border border-l-6 border-grey3 border-l-cofeeRed7 p-3">
          <Icon name="alert-circle" stroke={theme.cofeeRed7} size="24" />
          <div className="flex flex-col">
            <span className="text-base text-cofeeRed7">
              Account verification failed
            </span>
            <span className="text-sm text-cofeeNeutral10">
              {rejectedReason || 'Bank account name does not match PAN details'}
            </span>
          </div>
        </div>
      )}
      {!isDocumentFailed && currentStep === 0 && !isLite && (
        <BusinessTypeSelector
          selectedBusinessType={selectedBusinessType}
          setSelectedBusinessType={handleBusinessTypeChange}
        />
      )}
      <FormStepper steps={steps} currentStep={currentStep} />
      <div className="mt-6">
        {currentStep === 0 ? (
          <PanCardDetails
            pan={pan}
            onPanChange={onPanChange}
            panError={panError}
            panFile={panFile}
            panFileRef={panFileRef}
            handlePanFileSelect={handlePanFileSelect}
            removePanFile={removePanFile}
            onDocumentsChange={setSupportingDocs}
            supportingDocs={supportingDocs}
            remarks={remarks}
            onRemarksChange={setRemarks}
            existingDocs={existingDocs}
            onExistingDocDownload={handleExistingDocDownload}
            removeExistingPanDoc={removeExistingPanDoc}
            selectedBusinessType={selectedBusinessType}
            selectedLicenseType={businessLicenseType}
            onLicenseTypeChange={setBusinessLicenseType}
            businessLicenseFile={businessLicenseFile}
            businessLicenseFileRef={businessLicenseFileRef}
            handleBusinessLicenseFileSelect={handleBusinessLicenseFileSelect}
            removeBusinessLicenseFile={removeBusinessLicenseFile}
            removeExistingBusinessLicenseDoc={removeExistingBusinessLicenseDoc}
            orgName={orgName}
            setOrgName={setOrgName}
            certificateType={certificateType}
            setCertificateType={setCertificateType}
            onGstinChange={onGstinChange}
            gstin={gstin}
            gstinError={gstinError}
            gstinFile={gstinFile}
            gstinFileRef={gstinFileRef}
            handleGstinFileSelect={handleGstinFileSelect}
            removeGstinFile={removeGstinFile}
            onViewSample={() => setShowPreviewModal(true)}
          />
        ) : (
          <BankAccountDetails
            info={bankInfo}
            onInputChange={onBankInputChange}
            isLoadingAccountDetails={false}
            fieldError={fieldError}
            setFieldError={setFieldError}
            isVerified={isVerified}
            verifiedName={verifiedName}
            onEdit={handleBankDetailsEdit}
            onVerify={verifyBankDetails}
            onCancel={() => {
              setBankInfo({ ...savedBankInfo });
              setVerifiedName({
                ...savedVerifiedName,
                bankBranchName: savedVerifiedName.bankBranchName
              });
              setIsVerified(true);
              setIsEditing(false);
            }}
            isVerifyLoading={isVerifyBankLoading}
            isVerifyDisabled={isVerifyDisabled}
            isEditing={isEditing}
          />
        )}
      </div>
      {showPreviewModal && (
        <PreviewModal
          onClose={() => setShowPreviewModal(false)}
          flow="orgOnboard"
          analyticsFlow="kyc_via_banner"
        />
      )}
    </Modal>
  );
};

export default SubmitKycModal;
